import useCreateCartProduct from '@/hooks/useCreateCartProduct';
import { gql, useQuery } from '@apollo/client';
import breakpoints from '@/constants/breakpoints';
import React, { useEffect } from 'react';
import Imgix from 'react-imgix';
import styled from 'styled-components';
import Cart from '../../../common/Cart';
import { toast } from 'react-toastify';
import { toImgixUrl } from '../../../../../../utils/helper';
import ButtonLoading from '@/common/ButtonLoading';

const ProductContent = ({ product, updatePosition }) => {
  const { id, title, link, image } = product;

  const { loading, error, data } = useQuery(FIND_PRODUCT_BY_ID, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: parseInt(id)
    }
  });

  const productData = data?.findProductById;

  const [
    handleAddToCart,
    { loading: loadingAddToCart }
  ] = useCreateCartProduct();

  const addToCart = async () => {
    await handleAddToCart({
      product: productData,
      quantity: 1,
      source: 2
    });

    toast.success('Product added to cart');
  };

  return (
    <Container>
      <ImageWrapper
        href={link}
        rel="noopener nofollow noreferrer"
        target="_blank"
      >
        <Imgix
          className="product-image"
          loading="lazy"
          imgixParams={{ auto: 'compress,format', h: 32 }}
          height={32}
          sizes="32px"
          src={toImgixUrl(image || productData.images[0].path)}
        />
      </ImageWrapper>
      <Title
        className="title"
        href={link}
        rel="noopener nofollow noreferrer"
        target="_blank"
      >
        {title || productData.name}
      </Title>
      <AddToCartButton
        onClick={addToCart}
        disabled={loading || loadingAddToCart}
      >
        {loading ? <ButtonLoading radius={10}/> : <Cart />}
      </AddToCartButton>
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  z-index: 1001;
  min-height: 55px;
`;

const AddToCartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(204, 204, 204, 0.2);
  border-radius: 100%;
  background-color: transparent;
  width: 37px;
  height: 37px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);

  &:disabled {
    filter: grayscale(0.8);
  }

  @media (min-width: ${breakpoints['tablet']}px) {
    width: 30px;
    height: 37px;
  }
`;

const Title = styled.a`
    @media (min-width: ${breakpoints['tablet']}px) {
      gap: 10px;
    }
    font-family: ${({ theme }) => theme.fonts.primary};
    text-align: left;
    display: block;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: normal;
    line-height: 1;
    text-decoration: none;
    color: black;
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (min-width: ${breakpoints['tablet']}px) {
      max-width: 110px;
    }

    @media (min-width: ${breakpoints['desktop']}px) {
      max-width: 150px;
    }

    @media (min-width: ${breakpoints['large-desktop']}px) {
      max-width: 120px;
    }

    &:hover {
      cursor: pointer;
      color: black;
      text-decoration: underline;
    }
`;

const ImageWrapper = styled.a`
  display: block;
  height: 32px;
  width: auto;
  @media (min-width: ${breakpoints['tablet']}px) {
    display: none;
  }
  @media (min-width: ${breakpoints['medium-desktop']}px) {
    display: block;
    min-width: 57px;
  }
`;

const FIND_PRODUCT_BY_ID = gql`
  query findProductById($id: ID!) {
    findProductById(id: $id) {
      id
      sku
      name
      slug
      price
      images {
        id
        name
        path
      }
    }
  }
`;

export default ProductContent;
